// The camera's output, drawn.
//
// A real ALPR reader keeps a photograph. This county has no real traffic to
// photograph, so the still is reconstructed from the fingerprint the reader did
// keep -- make, color and body -- and drawn as a side-profile the way a
// surveillance capture frames one: IR-tinted, a targeting bracket, a timestamp,
// and the plate lifted into its own crop.
//
// The vehicle itself is generated, not picked from a handful of shapes. A make
// and a body select a silhouette (a Dodge sedan is a fastback muscle car, a
// Subaru is a raised wagon, a Jeep is an upright box, a RAM is a crew cab) and
// the make nudges its proportions and its wheels, so two silver SUVs from
// different makes do not draw the same. It is a drawing of a record, still, but
// it should read as a specific car.

const NS = 'http://www.w3.org/2000/svg';

/** Unique per-still id seed, so gradient ids never collide across cards. */
let stillSeq = 0;

/** The paint color, as the reader would name it, resolved to a swatch. */
export const VEHICLE_COLORS: Readonly<Record<string, string>> = {
  white: '#e9edf2', black: '#20242b', silver: '#c4cad2', gray: '#828b96',
  blue: '#3f6fd0', red: '#c8402f', green: '#2f8f57', tan: '#c9ad7f',
  maroon: '#7a2233',
};

export function vehicleHex(color: string): string {
  return VEHICLE_COLORS[color.toLowerCase()] ?? '#8a93a0';
}

// ---- generated sprites ------------------------------------------------------
//
// Photoreal vehicle sprites live in /vehicles/<make>-<body>.png, rendered at an
// elevated ALPR-camera angle. When one exists for a make+body it is drawn in
// place of the generated silhouette; anything without a sprite falls back to
// the drawing, so the set can fill in over time without breaking a card.

function slug(make: string): string {
  return make.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/** The make+body keys that have a real sprite on disk. Grows as art lands. */
const SPRITE_KEYS: ReadonlySet<string> = new Set<string>([
  'bmw-motorcycle',
  'buick-car',
  'buick-suv',
  'chevrolet-car',
  'chevrolet-pickup',
  'chevrolet-suv',
  'chevrolet-van',
  'chrysler-car',
  'chrysler-van',
  'dodge-car',
  'dodge-suv',
  'dodge-van',
  'ducati-motorcycle',
  'ford-car',
  'ford-pickup',
  'ford-suv',
  'ford-van',
  'gmc-pickup',
  'gmc-suv',
  'harley-davidson-motorcycle',
  'honda-car',
  'honda-motorcycle',
  'honda-suv',
  'honda-van',
  'hyundai-car',
  'hyundai-suv',
  'indian-motorcycle',
  'jeep-suv',
  'kawasaki-motorcycle',
  'kia-car',
  'kia-suv',
  'kia-van',
  'nissan-car',
  'nissan-pickup',
  'nissan-suv',
  'ram-pickup',
  'subaru-car',
  'subaru-suv',
  'suzuki-motorcycle',
  'toyota-car',
  'toyota-pickup',
  'toyota-suv',
  'toyota-van',
  'triumph-motorcycle',
  'volkswagen-car',
  'volkswagen-suv',
  'yamaha-motorcycle',
]);

export function spriteKey(make: string, body: string): string | null {
  const key = `${slug(make)}-${body}`;
  return SPRITE_KEYS.has(key) ? key : null;
}

// Recolored sprites, cached. The base PNGs are shot in neutral silver; the read
// color is applied in-browser by overlaying it onto the vehicle's own luminance
// (highlights and shadows preserved) so one silver base recolors to any read
// color -- colored bases would multiply sprite storage for nothing. The paint is
// GATED by luminance: only the mid-to-bright body panels take color, while dark
// pixels (tinted window glass, tires, black trim, deep shadow) are left as their
// own neutral dark selves. Without the gate the overlay blend multiplies the read
// color into the dark glass and the windows come out a dark shade of the paint.
// Cached by key+color.
const baseImages = new Map<string, HTMLImageElement>();
const tintCache = new Map<string, string>();

// Luminance band (0..255) over which a pixel ramps from "leave it dark and
// neutral" (below LO: glass/tires/trim) to "fully painted body" (above HI). The
// silver base puts window glass and rubber well under LO and lit panels above HI.
const PAINT_LO = 86;
const PAINT_HI = 134;
// Upper band: fade the paint back out for the brightest pixels so chrome and hot
// specular glints stay neutral instead of taking a color cast. This only catches
// BRIGHT chrome -- chrome's darker reflections read like body luminance and still
// take some color, which luminance alone cannot separate from silver paint.
const PAINT_CHROME_LO = 202;
const PAINT_CHROME_HI = 244;

function loadBase(key: string): HTMLImageElement {
  let img = baseImages.get(key);
  if (!img) {
    img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `/vehicles/${key}.png`;
    baseImages.set(key, img);
  }
  return img;
}

function tint(key: string, hex: string, done: (url: string) => void): void {
  const ck = `${key}|${hex}`;
  const cached = tintCache.get(ck);
  if (cached) {
    done(cached);
    return;
  }
  const base = loadBase(key);
  const paint = (): void => {
    const w = base.naturalWidth;
    const h = base.naturalHeight;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const x = c.getContext('2d');
    if (!x) return;
    x.drawImage(base, 0, 0);
    const img = x.getImageData(0, 0, w, h);
    const d = img.data;
    const [cr, cg, cb] = parse(hex);
    // overlay blend of one channel's base luminance (0..1) with the read color.
    const overlay = (lum: number, col: number): number => {
      const cc = col / 255;
      return (lum < 0.5 ? 2 * lum * cc : 1 - 2 * (1 - lum) * (1 - cc)) * 255;
    };
    // Dark read colors (black is #20242b) come out muddy grey through overlay
    // alone, because overlay preserves the silver base's bright highlights. Blend
    // the paint toward a straight multiply as the target darkens so black reads
    // black; normal colors (gray and up) stay pure overlay and keep their gloss.
    const tLum = (0.299 * cr! + 0.587 * cg! + 0.114 * cb!) / 255;
    let darkBlend = (0.32 - tLum) / 0.26;
    darkBlend = darkBlend < 0 ? 0 : darkBlend > 1 ? 1 : darkBlend;
    const paintCh = (ln: number, col: number): number => {
      const ov = overlay(ln, col);
      return ov + (ln * col - ov) * darkBlend;
    };
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] === 0) continue; // transparent: leave alone
      const r = d[i]!;
      const g = d[i + 1]!;
      const b = d[i + 2]!;
      // saturated warm pixels -- red tail lights, amber turn signals, tan/brown
      // leather -- keep their real color instead of taking the body paint. The
      // silver base is near-neutral (r~=g~=b) so panels never trip this.
      const mx = Math.max(r, g, b);
      const mn = Math.min(r, g, b);
      if (mx - mn > 32 && r === mx && b === mn) continue;
      const lum = 0.299 * r + 0.587 * g + 0.114 * b; // 0..255
      let wt = (lum - PAINT_LO) / (PAINT_HI - PAINT_LO);
      wt = wt < 0 ? 0 : wt > 1 ? 1 : wt;
      if (wt === 0) continue; // dark glass / tire / trim: keep it neutral
      wt = wt * wt * (3 - 2 * wt); // smoothstep for a soft body/glass edge
      // fade back out toward the brightest pixels: chrome / specular stay neutral
      let hi = (PAINT_CHROME_HI - lum) / (PAINT_CHROME_HI - PAINT_CHROME_LO);
      hi = hi < 0 ? 0 : hi > 1 ? 1 : hi;
      wt *= hi * hi * (3 - 2 * hi);
      if (wt === 0) continue; // bright chrome / specular: keep it neutral
      const ln = lum / 255;
      d[i] = r + (paintCh(ln, cr!) - r) * wt;
      d[i + 1] = g + (paintCh(ln, cg!) - g) * wt;
      d[i + 2] = b + (paintCh(ln, cb!) - b) * wt;
    }
    x.putImageData(img, 0, 0);
    const url = c.toDataURL('image/png');
    tintCache.set(ck, url);
    done(url);
  };
  if (base.complete && base.naturalWidth > 0) paint();
  else base.addEventListener('load', paint, { once: true });
}

// ---- color math -------------------------------------------------------------

function lighten(hex: string, t: number): string {
  return mix(hex, '#ffffff', t);
}
function darken(hex: string, t: number): string {
  return mix(hex, '#000000', t);
}
function mix(a: string, b: string, t: number): string {
  const pa = parse(a);
  const pb = parse(b);
  const c = (i: number): number => Math.round(pa[i]! + (pb[i]! - pa[i]!) * t);
  return `#${[c(0), c(1), c(2)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}
function parse(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** A tiny deterministic hash so a make maps to stable wheel/detail choices. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0);
}

// ---- the vehicle spec -------------------------------------------------------

type Roofline = 'notch' | 'fast' | 'hatch' | 'wagon' | 'box' | 'crossover' | 'truck' | 'van' | 'minivan';

interface Spec {
  roofline: Roofline;
  /** Overall body length in view units (rear bumper to front bumper span). */
  length: number;
  /** Ride height lift; SUVs and trucks sit higher. */
  clearance: number;
  /** Cabin roof height (smaller y = taller car). */
  roof: number;
  /** How far the cabin sits back, 0..1 across the body. */
  cabBack: number;
  /** Cabin length as a fraction of body length. */
  cabLen: number;
  /** Wheel radius. */
  wheelR: number;
  /** Number of side windows (DLO divisions). */
  windows: number;
  wheelStyle: number;
  /** Truck bed depth / van squareness, style-specific. */
  extra?: number;
}

// Archetypes: the shape a body-and-make resolves to. Numbers are tuned against
// a 260x132 frame with the rear bumper near x=22.
function specFor(make: string, body: string): Spec {
  const h = hash(make + body);
  const wheelStyle = h % 5;
  const jitter = ((h >> 3) % 5) - 2; // -2..2 length nudge per make

  if (body === 'car') {
    if (make === 'Dodge') {
      return { roofline: 'fast', length: 206 + jitter, clearance: 0, roof: 52, cabBack: 0.52, cabLen: 0.42, wheelR: 18, windows: 2, wheelStyle };
    }
    if (make === 'Volkswagen') {
      return { roofline: 'hatch', length: 176 + jitter, clearance: 1, roof: 49, cabBack: 0.5, cabLen: 0.46, wheelR: 17, windows: 2, wheelStyle };
    }
    if (make === 'Subaru') {
      return { roofline: 'wagon', length: 198 + jitter, clearance: 6, roof: 46, cabBack: 0.55, cabLen: 0.5, wheelR: 18, windows: 3, wheelStyle };
    }
    if (make === 'Buick' || make === 'Chrysler') {
      return { roofline: 'notch', length: 210 + jitter, clearance: 0, roof: 50, cabBack: 0.5, cabLen: 0.44, wheelR: 17, windows: 3, wheelStyle };
    }
    // Mainstream sedan.
    return { roofline: 'notch', length: 196 + jitter, clearance: 1, roof: 49, cabBack: 0.5, cabLen: 0.44, wheelR: 17, windows: 3, wheelStyle };
  }

  if (body === 'suv') {
    if (make === 'Jeep') {
      return { roofline: 'box', length: 190 + jitter, clearance: 12, roof: 40, cabBack: 0.5, cabLen: 0.56, wheelR: 20, windows: 2, wheelStyle: 3 };
    }
    if (make === 'Chevrolet' || make === 'GMC' || make === 'Ford' || make === 'Dodge') {
      return { roofline: 'box', length: 214 + jitter, clearance: 10, roof: 41, cabBack: 0.52, cabLen: 0.6, wheelR: 20, windows: 3, wheelStyle };
    }
    // Crossover: rounded roof, sloped tail.
    return { roofline: 'crossover', length: 198 + jitter, clearance: 8, roof: 43, cabBack: 0.53, cabLen: 0.56, wheelR: 19, windows: 3, wheelStyle };
  }

  if (body === 'pickup') {
    if (make === 'Toyota' || make === 'Nissan') {
      return { roofline: 'truck', length: 202 + jitter, clearance: 11, roof: 42, cabBack: 0.42, cabLen: 0.34, wheelR: 19, windows: 2, wheelStyle, extra: 0.9 };
    }
    // Full-size crew cab.
    return { roofline: 'truck', length: 222 + jitter, clearance: 13, roof: 41, cabBack: 0.44, cabLen: 0.4, wheelR: 21, windows: 2, wheelStyle: h % 3, extra: 1 };
  }

  if (body === 'van') {
    if (make === 'Ford' || make === 'Chevrolet') {
      return { roofline: 'van', length: 216 + jitter, clearance: 5, roof: 34, cabBack: 0.5, cabLen: 0.7, wheelR: 18, windows: 2, wheelStyle: 3, extra: 1 };
    }
    // Minivan.
    return { roofline: 'minivan', length: 210 + jitter, clearance: 4, roof: 40, cabBack: 0.52, cabLen: 0.66, wheelR: 18, windows: 4, wheelStyle };
  }

  // Motorcycle handled separately; return a sentinel spec.
  return { roofline: 'notch', length: 0, clearance: 0, roof: 0, cabBack: 0, cabLen: 0, wheelR: 0, windows: 0, wheelStyle };
}

// ---- svg helpers ------------------------------------------------------------

function el(name: string, attrs: Record<string, string | number>): SVGElement {
  const node = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

/** A detailed wheel: tire, sidewall, rim face with a spoke pattern, hub. */
function wheel(cx: number, cy: number, r: number, tint: string, style: number): SVGGElement {
  const g = el('g', {}) as SVGGElement;
  g.append(el('circle', { cx, cy, r, fill: '#0a0d12', stroke: '#04060a', 'stroke-width': 1 }));
  g.append(el('circle', { cx, cy, r: r - 3, fill: 'none', stroke: '#161b23', 'stroke-width': 2.5 }));
  const rim = r - 6;
  g.append(el('circle', { cx, cy, r: rim, fill: darken(tint, 0.2), stroke: '#3a4452', 'stroke-width': 1 }));
  g.append(el('circle', { cx, cy, r: rim, fill: 'none', stroke: 'rgba(255,255,255,0.18)', 'stroke-width': 0.8 }));
  const spokes = style === 0 ? 5 : style === 1 ? 6 : style === 2 ? 10 : style === 3 ? 8 : 5;
  const mesh = style === 4;
  for (let i = 0; i < spokes; i++) {
    const a = (i / spokes) * Math.PI * 2 - Math.PI / 2;
    if (mesh) {
      const a2 = ((i + 0.5) / spokes) * Math.PI * 2 - Math.PI / 2;
      g.append(el('line', {
        x1: cx + Math.cos(a) * 3, y1: cy + Math.sin(a) * 3,
        x2: cx + Math.cos(a2) * rim, y2: cy + Math.sin(a2) * rim,
        stroke: '#4a5563', 'stroke-width': 1.1,
      }));
    }
    g.append(el('line', {
      x1: cx + Math.cos(a) * 2.5, y1: cy + Math.sin(a) * 2.5,
      x2: cx + Math.cos(a) * rim, y2: cy + Math.sin(a) * rim,
      stroke: style === 3 ? '#525d6b' : '#454f5d', 'stroke-width': style === 2 ? 1.2 : 1.6,
    }));
  }
  g.append(el('circle', { cx, cy, r: 2.6, fill: '#5c6775', stroke: '#2a3038', 'stroke-width': 0.6 }));
  return g;
}

/** L-brackets in the corners: the reader locked onto the vehicle. */
function reticle(svg: SVGSVGElement): void {
  const b = 'rgba(255,176,32,0.85)';
  const bl = 16;
  const corners: [number, number, number, number][] = [
    [12, 26, 1, 1], [248, 26, -1, 1], [12, 118, 1, -1], [248, 118, -1, -1],
  ];
  for (const [x, y, sx, sy] of corners) {
    svg.append(el('path', { d: `M${x},${y + sy * bl} L${x},${y} L${x + sx * bl},${y}`, stroke: b, 'stroke-width': 1.4, fill: 'none' }));
  }
}

// ---- the body ---------------------------------------------------------------

interface Geometry {
  rearX: number;
  frontX: number;
  ground: number;
  bodyBottom: number;
  beltline: number;
  roofY: number;
  hoodY: number;
  cabFrontX: number;
  cabRearX: number;
  roofFrontX: number;
  roofRearX: number;
  wRearX: number;
  wFrontX: number;
  wheelR: number;
  wCy: number;
}

function geometry(spec: Spec): Geometry {
  const rearX = 22;
  const frontX = rearX + spec.length;
  const ground = 118;
  const lift = spec.clearance;
  const bodyBottom = 104 - lift * 0.2;
  const wheelR = spec.wheelR;
  const wCy = ground - wheelR - 1 + 0; // tires sit on the ground line
  const beltline = 68 - lift * 0.15;
  const roofY = spec.roof - lift * 0.2;
  const hoodY = beltline - 4;
  // The cabin. The rear (C-pillar) sits at the lower x; the front (A-pillar,
  // windshield) at the higher x, toward the front of the car. Faces right.
  const boxy = spec.roofline === 'box' || spec.roofline === 'van';
  const cabRearX = rearX + spec.length * (spec.cabBack - spec.cabLen / 2);
  const cabFrontX = cabRearX + spec.length * spec.cabLen;
  const roofFrontX = cabFrontX - (boxy ? 7 : 22);
  const roofRearX = cabRearX + (boxy ? 5 : 16);
  const wRearX = rearX + spec.length * 0.2;
  const wFrontX = frontX - spec.length * 0.2;
  return { rearX, frontX, ground, bodyBottom, beltline, roofY, hoodY, cabFrontX, cabRearX, roofFrontX, roofRearX, wRearX, wFrontX, wheelR, wCy };
}

function twoBox(spec: Spec): boolean {
  return !(spec.roofline === 'notch' || spec.roofline === 'fast');
}

/**
 * The whole car as one body-color silhouette -- roof and pillars included, the
 * way a real car is painted. The glass is punched into this shape afterward, so
 * the cabin reads as part of the body rather than a blob floating on top.
 */
function carOutline(g: Geometry, spec: Spec): string {
  const bb = g.bodyBottom;
  const belt = g.beltline;
  const roof = g.roofY;
  const af = g.cabFrontX;
  const ar = g.cabRearX;
  const rf = g.roofFrontX;
  const rr = g.roofRearX;
  const arch = g.wheelR + 2;
  const nose = g.hoodY + (spec.roofline === 'van' ? -3 : 6);

  let d = `M${g.rearX},${bb} `;
  if (twoBox(spec)) {
    // Rear rises to the roof: a hatch, wagon, SUV or van tail.
    const tail = spec.roofline === 'box' || spec.roofline === 'van' ? roof + 1 : roof + 4;
    d += `L${g.rearX},${belt + 2} `;
    d += `L${g.rearX + 1},${tail} `;
    d += `Q${g.rearX},${roof} ${g.rearX + 11},${roof} `;
    d += `L${rf},${roof} `;
  } else {
    // Three-box: a low trunk, then the greenhouse.
    d += `L${g.rearX},${belt + 8} `;
    d += `Q${g.rearX},${belt + 2} ${g.rearX + 11},${belt + 1} `;
    d += `L${ar},${belt} `;
    const backlightPull = spec.roofline === 'fast' ? 10 : 2;
    d += `Q${ar - 2},${roof + backlightPull} ${rr},${roof} `;
    d += `L${rf},${roof} `;
  }
  // Windshield down to the A-pillar, then cowl and hood to the nose.
  d += `Q${af - 1},${roof + 1} ${af + 3},${belt} `;
  d += `L${af + 7},${belt} `;
  d += `Q${af - 2},${g.hoodY} ${g.frontX - 20},${nose - 1} `;
  d += `Q${g.frontX - 3},${nose} ${g.frontX},${nose + 6} `;
  d += `L${g.frontX},${bb} `;
  // Bottom edge with wheel arches cut in.
  d += `L${g.wFrontX + arch},${bb} A${arch},${arch} 0 0 1 ${g.wFrontX - arch},${bb} `;
  d += `L${g.wRearX + arch},${bb} A${arch},${arch} 0 0 1 ${g.wRearX - arch},${bb} Z`;
  return d;
}

/** The side glass, punched into the cabin as a single dark daylight opening. */
function windowGlass(g: Geometry, spec: Spec): string {
  const belt = g.beltline - 3;
  const roof = g.roofY + 3.5;
  const af = g.cabFrontX + 6;
  const rf = g.roofFrontX + 3;
  const rr = g.roofRearX - 2;
  if (twoBox(spec)) {
    const rear = g.rearX + 8;
    return `M${af},${belt} Q${af - 1},${roof + 1} ${rf},${roof} L${rr + 4},${roof} L${rear + 2},${roof} L${rear},${belt} Z`;
  }
  const ar = g.cabRearX - 6;
  const backlightPull = spec.roofline === 'fast' ? 9 : 2;
  return `M${af},${belt} Q${af - 1},${roof + 1} ${rf},${roof} L${rr},${roof} Q${ar + 2},${roof + backlightPull} ${ar},${belt} Z`;
}

// ---- the still --------------------------------------------------------------

/**
 * The full still: the vehicle, framed and annotated like a capture. Returned as
 * one SVG so it scales crisply and self-contains every mark.
 */
export function vehicleStill(
  color: string,
  make: string,
  body: string,
  plate: string,
  jurisdiction: string,
): SVGSVGElement {
  const hex = vehicleHex(color);
  const uid = String(stillSeq++);
  const svg = el('svg', { viewBox: '0 0 260 132', class: 'cap-still' }) as SVGSVGElement;

  const defs = el('defs', {});
  const bodyGrad = el('linearGradient', { id: `b${uid}`, x1: 0, y1: 0, x2: 0, y2: 1 });
  bodyGrad.append(el('stop', { offset: '0%', 'stop-color': lighten(hex, 0.5) }));
  bodyGrad.append(el('stop', { offset: '34%', 'stop-color': lighten(hex, 0.08) }));
  bodyGrad.append(el('stop', { offset: '62%', 'stop-color': hex }));
  bodyGrad.append(el('stop', { offset: '100%', 'stop-color': darken(hex, 0.42) }));
  defs.append(bodyGrad);
  const glassGrad = el('linearGradient', { id: `g${uid}`, x1: 0, y1: 0, x2: 0, y2: 1 });
  glassGrad.append(el('stop', { offset: '0%', 'stop-color': '#324150' }));
  glassGrad.append(el('stop', { offset: '100%', 'stop-color': '#0e141d' }));
  defs.append(glassGrad);
  const vig = el('radialGradient', { id: `v${uid}`, cx: '50%', cy: '42%', r: '75%' });
  vig.append(el('stop', { offset: '55%', 'stop-color': 'rgba(0,0,0,0)' }));
  vig.append(el('stop', { offset: '100%', 'stop-color': 'rgba(0,0,0,0.55)' }));
  defs.append(vig);
  const pat = el('pattern', { id: `s${uid}`, width: 3, height: 3, patternUnits: 'userSpaceOnUse' });
  pat.append(el('rect', { width: 3, height: 1.2, fill: 'rgba(120,150,190,0.05)' }));
  defs.append(pat);
  svg.append(defs);

  const key = spriteKey(make, body);
  if (key) {
    drawSprite(svg, key, hex);
  } else if (body === 'motorcycle') {
    drawMotorcycle(svg, hex, make, uid);
  } else {
    drawCar(svg, hex, make, body, uid);
  }

  // Monitor grain + vignette over the vehicle.
  svg.append(el('rect', { x: 0, y: 0, width: 260, height: 132, fill: `url(#s${uid})` }));
  svg.append(el('rect', { x: 0, y: 0, width: 260, height: 132, fill: `url(#v${uid})` }));
  reticle(svg);
  plateCrop(svg, plate, jurisdiction);
  return svg;
}

/** A generated photoreal sprite, recolored to the read color, in the frame. */
function drawSprite(svg: SVGSVGElement, key: string, hex: string): void {
  svg.append(el('ellipse', { cx: 128, cy: 118, rx: 92, ry: 7, fill: 'rgba(0,0,0,0.4)' }));
  const img = el('image', {
    x: 14,
    y: -6,
    width: 214,
    height: 142,
    preserveAspectRatio: 'xMidYMid meet',
  }) as SVGImageElement;
  // Show the neutral base immediately; swap to the recolored version when ready.
  img.setAttribute('href', `/vehicles/${key}.png`);
  tint(key, hex, (url) => img.setAttribute('href', url));
  svg.append(img);
}

function drawCar(svg: SVGSVGElement, hex: string, make: string, body: string, uid: string): void {
  const spec = specFor(make, body);
  const g = geometry(spec);

  // Ground shadow.
  svg.append(el('ellipse', { cx: (g.rearX + g.frontX) / 2, cy: 119, rx: (g.frontX - g.rearX) / 2 + 4, ry: 6, fill: 'rgba(0,0,0,0.42)' }));

  // Wheels sit behind the body; arches reveal them.
  svg.append(wheel(g.wRearX, g.wCy, g.wheelR, hex, spec.wheelStyle));
  svg.append(wheel(g.wFrontX, g.wCy, g.wheelR, hex, spec.wheelStyle));

  // Truck bed opening, drawn before the body so the body frames it.
  if (spec.roofline === 'truck') {
    const bedTop = g.beltline + 2;
    svg.append(el('rect', { x: g.rearX + 4, y: bedTop, width: g.cabRearX - g.rearX - 10, height: g.bodyBottom - bedTop - 2, rx: 2, fill: darken(hex, 0.5) }));
  }

  // Body shell -- one body-color silhouette, roof and pillars included.
  const path = spec.roofline === 'truck' ? truckBodyPath(g, spec) : carOutline(g, spec);
  svg.append(el('path', { d: path, fill: `url(#b${uid})`, stroke: darken(hex, 0.5), 'stroke-width': 1, 'stroke-linejoin': 'round' }));

  // Glass punched into the cabin, with body-color pillars left standing across it.
  const glass = spec.roofline === 'truck' ? truckGlass(g) : windowGlass(g, spec);
  svg.append(el('path', { d: glass, fill: `url(#g${uid})`, stroke: 'rgba(0,0,0,0.35)', 'stroke-width': 0.8 }));
  const glassFront = g.cabFrontX + 6;
  const glassRear = twoBox(spec) ? g.rearX + 8 : g.cabRearX - 6;
  for (let i = 1; i < spec.windows; i++) {
    const x = glassFront + ((glassRear - glassFront) * i) / spec.windows;
    svg.append(el('rect', { x: x - 1.4, y: g.roofY + 2, width: 2.8, height: g.beltline - g.roofY - 2, fill: `url(#b${uid})`, stroke: darken(hex, 0.4), 'stroke-width': 0.4 }));
  }

  // Character line along the doors, and a rocker shadow at the sill.
  svg.append(el('path', {
    d: `M${g.rearX + 6},${g.beltline + 13} L${g.frontX - 14},${g.beltline + 11}`,
    stroke: 'rgba(255,255,255,0.14)', 'stroke-width': 1, fill: 'none',
  }));
  svg.append(el('path', {
    d: `M${g.wRearX + g.wheelR + 2},${g.bodyBottom - 1.5} L${g.wFrontX - g.wheelR - 2},${g.bodyBottom - 1.5}`,
    stroke: 'rgba(0,0,0,0.4)', 'stroke-width': 2, fill: 'none',
  }));

  // Door seam + handle.
  const doorX = (g.cabFrontX + g.cabRearX) / 2;
  svg.append(el('line', { x1: doorX, y1: g.beltline + 2, x2: doorX, y2: g.bodyBottom - 4, stroke: darken(hex, 0.42), 'stroke-width': 0.7, opacity: 0.55 }));
  svg.append(el('rect', { x: doorX - 9, y: g.beltline + 7, width: 7, height: 2, rx: 1, fill: darken(hex, 0.5) }));
  // Side mirror on the A-pillar.
  svg.append(el('path', { d: `M${g.cabFrontX + 4},${g.beltline + 1} l-6,-1 l0,4 l6,0 z`, fill: darken(hex, 0.35) }));

  // Head and tail lights.
  svg.append(el('path', { d: `M${g.frontX - 3},${g.hoodY + 5} l4,1 l0,6 l-4,1 z`, fill: '#fff3c4' }));
  svg.append(el('circle', { cx: g.frontX + 1, cy: g.hoodY + 9, r: 6, fill: '#fff3c4', opacity: 0.13 }));
  svg.append(el('rect', { x: g.rearX - 1, y: g.beltline + 7, width: 3, height: 7, rx: 1, fill: '#e8503a' }));
}

/** The cab glass for a pickup: windshield and door windows only. */
function truckGlass(g: Geometry): string {
  const belt = g.beltline - 3;
  const roof = g.roofY + 3.5;
  const af = g.cabFrontX + 6;
  const rf = g.roofFrontX + 3;
  const rr = g.roofRearX - 2;
  const back = g.cabRearX - 8;
  return `M${af},${belt} Q${af - 1},${roof + 1} ${rf},${roof} L${rr},${roof} L${back},${roof} L${back},${belt} Z`;
}

/** A pickup: cab greenhouse forward, an open bed behind, one continuous body. */
function truckBodyPath(g: Geometry, spec: Spec): string {
  const arch = g.wheelR + 3;
  const bb = g.bodyBottom;
  const bedRail = g.beltline + 2;
  const cabTop = g.roofY;
  const noseY = g.hoodY + 4;
  let d = `M${g.rearX},${bb} `;
  d += `L${g.rearX},${bedRail} `;
  d += `L${g.cabRearX - 4},${bedRail} `; // bed floor to cab back
  d += `L${g.cabRearX - 4},${g.beltline} `; // cab back wall up
  d += `Q${g.cabRearX - 6},${cabTop} ${g.roofRearX},${cabTop} `; // to roof back
  d += `L${g.roofFrontX},${cabTop} `; // roof
  d += `Q${g.cabFrontX},${cabTop} ${g.cabFrontX + 2},${g.beltline} `; // windshield down
  d += `L${g.cabFrontX + 6},${g.beltline} `;
  d += `Q${g.cabFrontX},${g.hoodY} ${g.frontX - 18},${noseY - 1} `; // hood
  d += `Q${g.frontX - 3},${noseY} ${g.frontX},${noseY + 6} `;
  d += `L${g.frontX},${bb} `;
  d += `L${g.wFrontX + arch},${bb} `;
  d += `A${arch},${arch} 0 0 1 ${g.wFrontX - arch},${bb} `;
  d += `L${g.wRearX + arch},${bb} `;
  d += `A${arch},${arch} 0 0 1 ${g.wRearX - arch},${bb} `;
  d += `Z`;
  return d;
}

function drawMotorcycle(svg: SVGSVGElement, hex: string, make: string, uid: string): void {
  const cruiser = make === 'Harley-Davidson' || make === 'Indian';
  const rearX = 62;
  const frontX = 196;
  const wCy = 100;
  const wheelR = cruiser ? 21 : 19;
  svg.append(el('ellipse', { cx: 129, cy: 119, rx: 74, ry: 6, fill: 'rgba(0,0,0,0.4)' }));
  // Wheels (open rims).
  for (const cx of [rearX, frontX]) {
    svg.append(el('circle', { cx, cy: wCy, r: wheelR, fill: 'none', stroke: '#10151c', 'stroke-width': 5 }));
    svg.append(el('circle', { cx, cy: wCy, r: wheelR - 4, fill: 'none', stroke: darken(hex, 0.2), 'stroke-width': 1 }));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      svg.append(el('line', { x1: cx, y1: wCy, x2: cx + Math.cos(a) * (wheelR - 4), y2: wCy + Math.sin(a) * (wheelR - 4), stroke: '#3f4a58', 'stroke-width': 1.2 }));
    }
    svg.append(el('circle', { cx, cy: wCy, r: 2.4, fill: '#5c6775' }));
  }
  if (cruiser) {
    // Long, low: raked forks, teardrop tank, stepped seat.
    svg.append(el('path', { d: `M${rearX},${wCy} L88,84 L120,80 L150,82 L${frontX - 4},${wCy - 2}`, fill: 'none', stroke: '#141a22', 'stroke-width': 4 }));
    svg.append(el('path', { d: 'M104,80 Q118,64 140,70 Q150,74 150,82 L106,86 Z', fill: `url(#b${uid})`, stroke: darken(hex, 0.5), 'stroke-width': 1 })); // tank
    svg.append(el('path', { d: 'M84,86 L106,84 L104,80 L86,80 Z', fill: '#1a2029' })); // seat
    svg.append(el('line', { x1: 150, y1: 82, x2: 176, y2: 58, stroke: '#2a323c', 'stroke-width': 3 })); // forks
    svg.append(el('path', { d: 'M168,54 l22,4', stroke: '#2a323c', 'stroke-width': 3 })); // bars
    svg.append(el('circle', { cx: 190, cy: 60, r: 4, fill: '#fff3c4' })); // headlamp
  } else {
    // Sport: forward mass, fairing, upswept tail.
    svg.append(el('path', { d: `M${rearX},${wCy} L86,80 L150,76 L${frontX},${wCy}`, fill: 'none', stroke: '#141a22', 'stroke-width': 4 }));
    svg.append(el('path', { d: 'M96,82 L150,74 Q168,72 176,84 L182,96 L150,92 L108,92 Z', fill: `url(#b${uid})`, stroke: darken(hex, 0.5), 'stroke-width': 1 })); // fairing/tank
    svg.append(el('path', { d: 'M78,80 L100,78 L98,84 L80,86 Z', fill: `url(#b${uid})`, stroke: darken(hex, 0.5), 'stroke-width': 0.8 })); // tail
    svg.append(el('path', { d: 'M176,84 l8,-8', stroke: '#2a323c', 'stroke-width': 3 }));
    svg.append(el('path', { d: 'M176,86 L186,90 L182,96 Z', fill: '#dfe6ef' })); // headlight
  }
}

/** The plate lifted out and enlarged, with a leader from the car. */
function plateCrop(svg: SVGSVGElement, plate: string, jurisdiction: string): void {
  const b = 'rgba(255,176,32,0.85)';
  const cropX = 162;
  const cropY = 96;
  const cropW = 86;
  const cropH = 26;
  svg.append(el('line', { x1: 232, y1: 84, x2: cropX + 6, y2: cropY, stroke: b, 'stroke-width': 0.8, 'stroke-dasharray': '2 2', opacity: 0.8 }));
  svg.append(el('rect', { x: cropX, y: cropY, width: cropW, height: cropH, rx: 2, fill: '#f2f4f7', stroke: b, 'stroke-width': 1.2 }));
  const juris = el('text', { x: cropX + 6, y: cropY + 11, fill: '#5b6472', 'font-size': 7, 'font-family': 'monospace', 'letter-spacing': '0.5' });
  juris.textContent = jurisdiction;
  svg.append(juris);
  const num = el('text', { x: cropX + 6, y: cropY + 22, fill: '#12161c', 'font-size': 13, 'font-weight': '700', 'font-family': 'monospace', 'letter-spacing': '1' });
  num.textContent = plate;
  svg.append(num);
}
